<template>
  <MapBoxPopup
    :coordinates="coordinates"
    :show="show"
    :offset="[0, -30]"
    @close="handleClose"
  >
    <div>
      <div
        v-for="chargingPoint in visibleChargingPoints"
        :key="chargingPoint ? chargingPoint.uuid : 'none'"
        class="d-flex flex-column mb-1"
      >
        <div v-if="chargingPoint">
          <div v-if="chargingPoint.address">
            {{ chargingPoint.address }}
          </div>
          <div>
            <span>{{ chargingPoint.statusLabel }}</span>
            <img
              :src="statusImage({ chargingPoint} )"
              class="ml-2"
              width="15"
            >
          </div>
          <div>EVtools ID: {{ `${chargingPoint.code}-${chargingPoint.id}` }}</div>
          <div v-if="!differentCpo({ chargingPoint })">
            CPO: {{ chargingPoint.cpo || $t('none') }}
          </div>
          <div v-if="chargingPoint.meta.source === 'import-royal-haskoning'">
            Type: {{ chargingPoint.meta.type }}
          </div>
          <div v-if="chargingPoint.meta.voltID">
            VOLT {{ $t('Information.location') }}: {{ chargingPoint.meta.voltID }}
          </div>
          <div v-if="chargingPoint.meta.phase">
            {{ $t('Information.phase') }}: {{ chargingPoint.meta.phase }}
          </div>
          <div v-if="chargingPoint.parkinglotIds">
            {{ $t('recordList.parkinglotIds') }}: {{ chargingPoint.parkinglotIds }}
          </div>
          <div
            v-if="isRealisationLocation({ chargingPoint })"
            class="mt-2"
          >
            {{ $t('Information.selectedLocation') }}
          </div>
          <div
            v-if="!locked"
            class="mt-2"
          >
            <Button
              v-if="canDisconnectLocation({ chargingPoint })"
              type="button"
              icon="fa-light fa-location-dot-slash"
              :label="$t('Information.disconnectLocation')"
              class="p-button-sm p-button-secondary w-100 mt-3 popup"
              @click="handleSelectLocation({ uuid: null }, $event)"
            />
            <Button
              v-else-if="canBeSelected({ chargingPoint })"
              type="button"
              icon="fa-light fa-location-dot"
              :label="$t('Information.useThisLocation')"
              class="p-button-sm p-button-success w-100 mt-3 popup"
              @click="handleSelectLocation({ uuid: chargingPoint.uuid }, $event)"
            />
          </div>
          <a
            v-if="hasWorkflowUuid({ uuid: chargingPoint.workflowUuid }) && hasValidCaseRef({ caseRef: chargingPoint.workflowCaseRef })"
            href=""
            class="d-flex align-items-center link-rp"
            @click.prevent="handleGoToProcess({ chargingPoint })"
          >
            {{ $t('Information.goToProcess') }} <strong>{{ chargingPoint.workflowCaseRef }}</strong>
            <img
              v-if="chargingPoint.isRealisationCancelled"
              :src="getImage('cancelled')"
              width="10"
            >
            <b-spinner
              v-if="openingProcessUuid === chargingPoint.workflowUuid"
              small
              variant="secondary"
            />
          </a>
          <div
            v-if="chargingPoint.remark"
            class="mt-3"
          >
            {{ chargingPoint.remark }}
          </div>
          <Button
            v-if="canLocationBeDeleted({ chargingPoint })"
            type="button"
            icon="fa-light fa-location-dot-slash"
            :label="$t('Information.deleteLocation')"
            class="p-button-sm p-button-danger w-100 mt-3 popup"
            @click="handleDeleteChargingPoint({ chargingPoint })"
          />
          <div
            v-if="canAccessDebugger"
            class="mt-3"
          >
            UUID: {{ chargingPoint.uuid }}
          </div>
        </div>
      </div>

      <b-pagination
        v-if="chargingPoints.length > 1"
        v-model="currentPage"
        class="justify-content-center align-items-center pagination-sm mt-3 mb-0"
        hide-goto-end-buttons
        :total-rows="chargingPoints.length"
        :per-page="1"
      />
    </div>
  </MapBoxPopup>
</template>

<script>
import MapBoxMixin from '@/components/common/MapBoxMixin.vue';
import MapBoxPopup from '@/components/common/MapBoxPopup';
import { mapGetters, mapActions } from 'vuex';
import { image } from '@/helpers/assets';
import Button from 'primevue/button'
import { processRouteTo } from '@/helpers/processRoute';
import { getProcessByUuid } from '@/services/processes';

export default {
  name: 'PopupChargingLocation',
  components: {
    MapBoxPopup,
    Button
  },
  mixins: [MapBoxMixin],
  props: {
    record: {
      type: Object,
      default: () => {}
    },
    locked: {
      type: Boolean,
      default: true
    },
    selected: {
      type: String,
      default: null
    },
    alternative: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      layerName: 'chargingpoints',
      show: false,
      uuids: [],
      currentPage: 1,
      openingProcessUuid: null,
      loadedChargingPoints: []
    };
  },
  computed: {
    ...mapGetters('chargingpoints', ['getChargingPointByUUID']),
    ...mapGetters('realisations', ['recordByUuid', 'records']),
    ...mapGetters('user', ['securityCheck', 'canChangeProcessLocation']),
    ...mapGetters('tenant', ['getCpos', 'getTenant', 'getChargingpointStatuses']),
    ...mapGetters('processes', ['getCurrentProcessType']),

    chargingPoints() {
      const chargingpoints = this.loadedChargingPoints.map(chargingPoint => {
          if (!chargingPoint) return;

          const data = chargingPoint;
          const status = data.properties.status;
          const statusLabel = this.getChargingpointStatuses.find(s => s.value === status.replace('-alternative', ''))?.label?.[this.$i18n.locale] || ''

          let location = {
            uuid: data.uuid,
            coordinates: data.coordinates,
            address: this.formatAddress(data),
            status,
            cpo: data.properties.stakeholders?.find(s => s.category === 'cpo')?.name || data.properties.operator,
            statusLabel,
            id: data.properties.id,
            code: data.code,
            remark: !data.properties.remark?.includes(data.properties.workflowCaseRef) ? data.properties.remark : '',
            isWorkflowCreated: data.properties.isWorkflowCreated || data.isWorkflowCreated,
            stakeholders: data.properties.stakeholders,
            ref: chargingPoint._id,
            meta: {
              source: null,
              type: null
            },
            selected: data.uuid === this.selected,
            workflowUuid: data.workflowUuid,
            workflowCaseRef: data.workflowCaseRef,
            isAsset: data.properties.isAsset
          };

          const linkedToCancelledRealisation = this.records.find(
            rp => rp.status.cancelled && rp.CurrentLocation.Location.chargingpointUuid === location.uuid
          );

          if (linkedToCancelledRealisation) {
            location.workflowUuid = linkedToCancelledRealisation.uuid;
            location.workflowCaseRef = linkedToCancelledRealisation.case_ref;
            location.isRealisationCancelled = true;
          }

          if (data.isWorkflowAltLocation) {
            location.isWorkflowAltLocation = data.isWorkflowAltLocation;
            location.remark = `${this.$i18n.t('Information.selectedAsAlternative')} (${data.workflowCaseRef})`;
          }

          if (data.properties.parkinglotIds) {
            location.parkinglotIds = data.properties.parkinglotIds.join(', ');
          }

          return location;
        });
      return chargingpoints
    },
    realisationHasLocation() {
      return this.record.CurrentLocation.Location?.chargingpointUuid;
    },
    visibleChargingPoints() {
      return [this.chargingPoints[this.currentPage - 1] || false];
    },
    coordinates() {
      return this.chargingPoints?.[0]?.coordinates || [0, 0];
    },
    hasRequiredInformation() {
      return this.chargingPoints?.length !== 0;
    },
    canAccessDebugger() {
      return this.securityCheck({
        attribute: 'canAccessDebugger'
      });
    },
    canDeleteChargingPoint() {
      return this.securityCheck({
        attribute: 'canDeleteProcess'
      });
    },
    isStep1() {
      return /Step1$/.test(this.$route.name);
    },
    tenantCpos() {
      return this.getCpos.map(cpo => cpo.value)
    },
    statusType () {
      return this.record.category === 'private' ? '-private' : ''
    },
    isRealisationType () {
      const processType = this.getCurrentProcessType || this.record?.processType?.value || this.$route.name.split('-')?.pop()
      return ['realisation', 'brussels', 'wallonia', 'takeover'].includes(processType)
    }
  },
  watch: {
    hasRequiredInformation(hasRequiredInformation) {
      this.show = hasRequiredInformation;
    }
  },
  async created() {
    this.bind();
  },
  beforeDestroy() {
    this.unbind();
  },
  methods: {
    ...mapActions('chargingpoints', ['deleteChargingPoint', 'fetchChargingPointsByUuid']),

    getImage(name) {
      return require(`@/assets/image/legend/realisation-${name}.png`);
    },

    canDisconnectLocation ({ chargingPoint }) {
      return this.canChangeProcessLocation && chargingPoint.selected && !chargingPoint.isAsset && this.hasWorkflowUuid({ uuid: chargingPoint.uuid })
    },

    statusImage({ chargingPoint }) {
      return image({ name: `chargingpoint-${chargingPoint.status}` });
    },

    differentCpo ({ chargingPoint }) {
      return chargingPoint.cpo === 'different-cpo'
    },

    hasValidCaseRef ({ caseRef }) {
      return caseRef && [this.getTenant.acronym, 'RP'].some(type => caseRef.startsWith(type))
    },

    async handleGoToProcess ({ chargingPoint }) {
      const uuid = chargingPoint.workflowUuid
      if (this.openingProcessUuid) return

      this.openingProcessUuid = uuid

      try {
        const token = await this.$auth.getTokenSilently()
        const process = await getProcessByUuid({ token, uuid, setSelected: false })

        if (!process || process.errorMessage) {
          throw new Error(process?.errorMessage || 'Process not found')
        }

        this.show = false
        this.$router.push(processRouteTo({
          uuid: process.uuid,
          processType: process.processType,
          currentStep: process.processStatus?.currentStep
        })).catch(() => {})
      } catch (error) {
        console.log(error)
        this.$store.dispatch('tenant/setToast', {
          vm: this,
          message: this.$i18n.t('Process.404-notFound'),
          error: true,
          delay: 100
        })
      } finally {
        this.openingProcessUuid = null
      }
    },

    formatAddress(data) {
      // Vlaanderen doesn't have formated_address, import_meta.label used in stead //
      return (data.address?.formatted_address + '').replace(', Nederland', '') || data.import_meta?.label;
    },
    bind() {
      if (!this.map) return;

      // click event
      this.map.on('click', this.layerName, this.handleLayerClickEvent);
      this.map.on('click', `${this.layerName}-text`, this.handleLayerClickEvent);

      // Cursor
      this.map.on('mouseenter', this.layerName, this.showPointer);
      this.map.on('mouseenter', `${this.layerName}-text`, this.showPointer);
      this.map.on('mouseleave', this.layerName, this.hidePointer);
      this.map.on('mouseleave', `${this.layerName}-text`, this.hidePointer);
    },
    unbind() {
      if (!this.map) return;

      this.map.off('click', this.layerName, this.handleLayerClickEvent);
      this.map.off('click', `${this.layerName}-text`, this.handleLayerClickEvent);

      this.map.off('mouseenter', this.layerName, this.showPointer);
      this.map.off('mouseenter', `${this.layerName}-text`, this.showPointer);
      this.map.off('mouseleave', this.layerName, this.hidePointer);
      this.map.off('mouseleave', `${this.layerName}-text`, this.hidePointer);
    },

    isRealisationLocation({ chargingPoint }) {
      const uuid = this.$route.params.uuid;
      return uuid && chargingPoint.workflowUuid === uuid && !chargingPoint.isRealisationCancelled;
    },

    canBeSelected({ chargingPoint }) {
      if (!this.canChangeProcessLocation) return false;
      if (!this.isRealisationType) {
        return false
      }

      const cpo =  chargingPoint.stakeholders?.find(stakeholder => stakeholder.category === 'cpo')
      const hasValidStakeholder = cpo && this.tenantCpos.includes(cpo.uuid)

      return (
        !chargingPoint.isWorkflowAltLocation &&
        ['suggestion', 'definitive'].some(keyword => chargingPoint.status.includes(keyword)) &&
        !this.differentCpo({ chargingPoint }) &&
        !chargingPoint.workflowUuid &&
        !this.selected &&
        !chargingPoint.meta?.voltID &&
        (!cpo || hasValidStakeholder)
      );
    },

    /**
     * Mouse hover effects
     */
    showPointer() {
      this.map.getCanvas().style.cursor = 'pointer';
    },
    hidePointer() {
      this.map.getCanvas().style.cursor = '';
    },

    handleLayerClickEvent(e) {
      if (!e.features.length) return;

      // Cancel other map events
      e.preventDefault();
      e.originalEvent.stopPropagation();

      let features = e.features;

      this.$nextTick(async () => {
        this.currentPage = 1;
        this.uuids = features.filter(feature => feature?.properties.uuid).map(feature => feature.properties.uuid);

        if (this.uuids.length) {
          this.loadedChargingPoints = await this.fetchChargingPointsByUuid({ uuids: this.uuids })
          this.show = true;
        }
      });
    },

    handleClose() {
      this.show = false;
      // this.uuids = []
    },

    handleSelectLocation({ uuid }, e) {
      e.preventDefault();
      this.$emit('location', { uuid });
      this.show = false;
    },

    hasWorkflowUuid({ uuid }) {
      return uuid && uuid !== this.$route.params.uuid;
    },

    handleDeleteChargingPoint({ chargingPoint }) {
      this.$bvModal
        .msgBoxConfirm(this.$i18n.t('Information.confitmLocationDelete'), {
          okVariant: 'danger',
          okTitle: this.$i18n.t('confirm'),
          cancelTitle: this.$i18n.t('cancel')
        })
        .then(confirmed => {
          if (confirmed) {
            const ref = chargingPoint.ref;
            this.deleteChargingPoint({ ref });
            this.handleClose();
          }
        });
    },

    canLocationBeDeleted({ chargingPoint }) {
      if (!this.canDeleteChargingPoint) return

      const eligibleStatuses = /definitive|suggestion|rejected/;
      const stakeholder = chargingPoint.stakeholders?.find(s => s.category === 'cpo')

      return (
        //this.isStep1 &&
        chargingPoint.isWorkflowCreated &&
        eligibleStatuses.test(chargingPoint.status) &&
        this.tenantCpos.includes(stakeholder?.uuid) &&
        !chargingPoint.isWorkflowAltLocation &&
        (!chargingPoint.workflowUuid || chargingPoint.isRealisationCancelled) &&
        chargingPoint.uuid !== this.selected &&
        !this.isAlternativeLocation({ chargingPoint })
      );
    },

    isWorkflowCreated({ source }) {
      return source === process.env.VUE_APP_TENANT;
    },

    isAlternativeLocation({ chargingPoint }) {
      return this.record.CurrentLocation.CPOAdvice?.Alternative?.Location?.chargingpointUuid === chargingPoint.uuid;
    }
  }
};
</script>
<style lang="scss" scoped>
.link-rp {
  gap: 0.3em;
}
::v-deep .p-button-sm.popup {
  padding: 0.2em 1em;
  .p-button-icon-left {
    margin-right: 0;
  }
}
::v-deep .p-button.p-button-secondary, .p-buttonset.p-button-secondary > .p-button, .p-splitbutton.p-button-secondary > .p-button {
    background: #6c757d;
    border: 1px solid #6c757d;
    &:hover {
      background: #666;
      border: 1px solid #6c757d;
    }
  }
</style>
